import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Send, Ticket, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "@/contexts/AuthContext";
import { 
  useStartChatSession, 
  useSendChatMessage, 
  useChatMessages,
  useChatMessagesSubscription,
  useConvertChatToTicket
} from "@/hooks/useSupportChat";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

interface SupportChatWindowProps {
  session: {
    id: string;
    status: string;
  } | null | undefined;
  onEscalateToTicket: () => void;
}

export const SupportChatWindow = ({ session, onEscalateToTicket }: SupportChatWindowProps) => {
  const [message, setMessage] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const startSession = useStartChatSession();
  const sendMessage = useSendChatMessage();
  const convertToTicket = useConvertChatToTicket();
  const { data: messages = [], isLoading } = useChatMessages(session?.id);
  
  // Subscribe to real-time updates
  useChatMessagesSubscription(session?.id);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!message.trim()) return;

    try {
      // If no session, start one first
      if (!session) {
        const newSession = await startSession.mutateAsync(message.trim());
        if (newSession) {
          // Session created with initial message
        }
      } else {
        await sendMessage.mutateAsync({
          sessionId: session.id,
          content: message.trim()
        });
      }
      setMessage("");
    } catch (error) {
      toast.error("Erreur lors de l'envoi du message");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleEscalate = async () => {
    if (!session) return;
    
    try {
      const ticket = await convertToTicket.mutateAsync({
        sessionId: session.id,
        subject: "Demande depuis le chat",
        category: "help"
      });
      toast.success("Ticket créé avec succès");
      navigate(`/support?ticket=${ticket.id}`);
    } catch (error) {
      toast.error("Erreur lors de la création du ticket");
    }
  };

  return (
    <div className="flex flex-col h-[400px]">
      {/* Messages Area */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary/20 to-coral/20 flex items-center justify-center mb-4">
              <Send className="h-7 w-7 text-primary" />
            </div>
            <h4 className="font-medium mb-1">Bienvenue !</h4>
            <p className="text-sm text-muted-foreground">
              Envoyez un message pour démarrer une conversation avec notre équipe support.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((msg) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${msg.sender_id === user?.id ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    msg.sender_id === user?.id
                      ? "bg-primary text-primary-foreground rounded-br-md"
                      : "bg-secondary rounded-bl-md"
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  <p className={`text-[10px] mt-1 ${
                    msg.sender_id === user?.id ? "text-primary-foreground/70" : "text-muted-foreground"
                  }`}>
                    {format(new Date(msg.created_at), "HH:mm", { locale: fr })}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Actions */}
      {session && (
        <div className="px-4 pb-2">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-muted-foreground hover:text-foreground gap-1"
            onClick={handleEscalate}
            disabled={convertToTicket.isPending}
          >
            <Ticket className="h-3 w-3" />
            Créer un ticket formel
          </Button>
        </div>
      )}

      {/* Input Area */}
      <div className="p-4 border-t border-border">
        <form 
          onSubmit={(e) => { e.preventDefault(); handleSend(); }}
          className="flex items-center gap-2"
        >
          <Input
            placeholder="Tapez votre message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            className="flex-1"
            disabled={sendMessage.isPending || startSession.isPending}
          />
          <Button 
            type="submit" 
            size="icon"
            disabled={!message.trim() || sendMessage.isPending || startSession.isPending}
          >
            {sendMessage.isPending || startSession.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};
